/**
 * event_type-driven vocabulary.
 *
 * The registration flow is one state machine, but the words differ by
 * event type: a club tournament registers a "team"; a charity event
 * registers a unit-neutral "entry" (the organizer's division options name
 * the actual unit — foursome, individual, table, …). Rather than fork the
 * pages, each page pulls its nouns from useVocab(), which resolves the
 * current event's event_type from the embed config.
 *
 * Config is provided once at the App root; useVocab() reads it plus the
 * :eventId route param so any flow page gets the right wording with no
 * prop threading.
 */
import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import { useParams } from 'react-router-dom';

import type { ConfigResponse } from '@/api/embed';

export interface Vocab {
  isCharity: boolean;
  /** singular, lowercase — "team" | "entry" */
  entrant: string;
  /** singular, capitalized — "Team" | "Entry" */
  entrantCap: string;
  /** plural, lowercase — "teams" | "entries" */
  entrantPlural: string;
  /** indefinite article matching `entrant` — "a" | "an" */
  entrantArticle: string;
  /** fork CTA label — "Register a team" | "Register". Deliberately does
   *  NOT presume the entry unit for charity: the organizer's division
   *  names (a foursome, an individual, a table, …) carry the specifics. */
  registerCta: string;
  /** singular, lowercase — "division" */
  division: string;
  /** singular, capitalized — "Division" */
  divisionCap: string;
  /** plural, lowercase — "divisions" */
  divisionPlural: string;
  /** capacity unit, singular — "team" | "spot" */
  capacityNoun: string;
  /** name-field placeholder for the create form */
  namePlaceholder: string;
}

const CLUB: Vocab = {
  isCharity: false,
  entrant: 'team',
  entrantCap: 'Team',
  entrantPlural: 'teams',
  entrantArticle: 'a',
  registerCta: 'Register a team',
  division: 'division',
  divisionCap: 'Division',
  divisionPlural: 'divisions',
  capacityNoun: 'team',
  namePlaceholder: 'e.g. Hartford Thunder U10',
};

// Charity events cover many formats (golf scrambles, 5Ks, galas, …), so
// the wording stays unit-neutral — "entry" rather than a golf "foursome".
// The organizer's division options name the actual unit.
const CHARITY: Vocab = {
  isCharity: true,
  entrant: 'entry',
  entrantCap: 'Entry',
  entrantPlural: 'entries',
  entrantArticle: 'an',
  registerCta: 'Register',
  // Charity events use "option" rather than the sports-flavored "division"
  // — reads naturally for a gala/run/golf alike (the organizer's option
  // names carry the specifics: foursome, individual, table, …).
  division: 'option',
  divisionCap: 'Option',
  divisionPlural: 'options',
  capacityNoun: 'spot',
  namePlaceholder: 'e.g. The Johnson Group',
};

export function vocabForEventType(eventType: string | undefined | null): Vocab {
  return eventType === 'charity' ? CHARITY : CLUB;
}

// ── config context ────────────────────────────────────────────────────

const ConfigContext = createContext<ConfigResponse | null>(null);

export function ConfigProvider({
  config,
  children,
}: {
  config: ConfigResponse | null;
  children: ReactNode;
}) {
  return <ConfigContext.Provider value={config}>{children}</ConfigContext.Provider>;
}

export function useConfig(): ConfigResponse | null {
  return useContext(ConfigContext);
}

/** Vocabulary for the event in the current :eventId route. Falls back to
 *  club wording when the event/type can't be resolved. */
export function useVocab(): Vocab {
  const config = useConfig();
  const { eventId } = useParams<{ eventId: string }>();
  const eventType = config?.allowed_events.find((e) => e.id === eventId)?.event_type;
  return vocabForEventType(eventType);
}
